/**
 * Derivações puras do Dashboard M1 (GUPM-191 — fidelidade ao V8). TS puro:
 * sem React/Supabase/DOM. A UI (useM1Data) só busca dados e chama estas funções.
 */
import type { Canal, TarpLevel } from '../types';
import {
  FORA_DE_SERVICO, leituraGovernante, SEM_LEITURA, semSinalVisivel, temFaixaDeControle,
  type NivelExibido,
} from '../instrumentos/nivel-exibido';
import { ehMarco } from '../instrumentos/linhas-de-marco';
import { unitForType } from '../instrumentos/instrument-type';
import { foraDeServico } from '../instrumentos/fora-de-servico';

export * from './anomalias';

// Shapes mínimos (desacoplados de Instrument/Reading do banco — como `eventos`).
interface InstLike {
  id: string;
  type: string;
  /** GUPM-247 — `null` = sem carta de controle; `ok` exige faixa (ver `tiltimetroResumo`). */
  thresholdsSource: string | null;
  /** GUPM-270 — retirado fica fora do denominador (ver `tiltimetroResumo`). */
  isActive: boolean;
  /** GUPM-303 — instrumento mudo não conta como `ok` (ver `tiltimetroResumo`). */
  isOnline: boolean | null;
  /** GUPM-307 — declarado fora de serviço sai do denominador (ver `tiltimetroResumo`). */
  foraDeServicoDesde: Date | null;
  /** GUPM-270 — canais com carta vigente (tiltímetro só tem carta nos eixos). */
  canaisComFaixa?: readonly Canal[];
}
interface CurrentReadingLike {
  instrumentId: string;
  level: string | null;
}
interface FeedReadingLike {
  instrumentId: string;
  value: number;
  ts: Date;
  /** GUPM-270: a comparação "mudou de valor?" é POR CANAL (ver `ultimaLeituraAlterada`). */
  canal?: Canal | null;
}
interface LevelReadingLike {
  level: string | null;
  ts: Date;
}

export interface TiltResumo {
  ok: number;
  total: number;
  pct: number;
}

/**
 * Resumo dos tiltímetros (D5): `total` = nº de instrumentos TILT; `ok` = TILT cuja
 * leitura atual é comprovadamente NORMAL; `pct` = round(ok/total*100). Fiel ao card
 * compacto de tiltímetros do V8 ("ok/total").
 *
 * **`ok` exige lastro** — a regra que esta função já aplicava para ausência de
 * leitura ("perda de sinal NÃO conta como ok") vale igual para ausência de faixa:
 *
 * - sem leitura → não conta (perda de sinal);
 * - **sem faixa de controle → não conta** (GUPM-247). O `level` gravado em
 *   `readings` é `NORMAL` fabricado nesse caso — a ingestão insere NORMAL
 *   provisório e `classify_reading_level` devolve `null` sem carta vigente. Os 14
 *   tiltímetros do banco real estão TODOS sem faixa, então o card exibia
 *   "100% · Tiltímetros OK" em VERDE sobre nada verificado.
 *
 * Consequência assumida: com o dado de hoje o card mostra 0% em laranja. É
 * conservador na direção certa — laranja pede investigação, verde encerra o
 * assunto. O rótulo "operacionais" do card ficou impreciso (um tiltímetro em
 * ATENÇÃO opera perfeitamente e não conta como ok); definir "operacional" por
 * frescor de leitura em vez de severidade é o **GUPM-248**.
 *
 * **GUPM-303 — instrumento MUDO também não conta.** A regra "sem leitura não conta"
 * cobria só a ausência de linha em `readings_current`; o tiltímetro que emudeceu TEM
 * linha — a última, congelada, com `level` NORMAL e faixa — e por isso entrava em `ok`.
 * O defeito foi visto na tela: com o TIL-05 mudo, o bloco "Precisa da sua atenção"
 * dizia "Tiltímetro sem sinal há 7h 36min" e este card, dois centímetros abaixo, dizia
 * "100% · 10/10 operacionais". Duas afirmações contraditórias sobre o mesmo sensor, na
 * mesma tela.
 */
export function tiltimetroResumo(instruments: InstLike[], current: CurrentReadingLike[]): TiltResumo {
  // GUPM-270 — só tiltímetro ATIVO entra no denominador. Os 4 retirados não têm carta
  // nem leitura corrente, então nunca podiam entrar em `ok`: a métrica ficava presa em
  // 71% (10/14) para sempre, mesmo com os 10 existentes todos normais. Indicador
  // travado num número ruim ou é ignorado — e aí não serve — ou alarma sem motivo.
  // GUPM-307 — declarado fora de servico sai do denominador, pelo mesmo motivo do
  // retirado: ele nao esta operando, e mante-lo em `total` trava a metrica num numero
  // ruim que nao depende de ninguem. Este ponto NAO e pego pelo compilador — a regra
  // aqui e um `filter` e um predicado, nao um `Record<NivelExibido, …>`, e foi
  // exatamente por isso que o GUPM-303 chegou a dizer "10/10 operacionais" dois
  // centimetros abaixo de "tiltimetro sem sinal ha 7h36".
  const tilts = instruments.filter(
    (i) => i.type === 'TILT' && i.isActive && !foraDeServico(i),
  );
  const total = tilts.length;
  const atualPorId = new Map(current.map((r) => [r.instrumentId, r]));
  const ok = tilts.filter((t) => {
    const atual = atualPorId.get(t.id);
    return (
      atual != null
      && !semSinalVisivel(t)
      && temFaixaDeControle(t)
      && atual.level === 'NORMAL'
    );
  }).length;
  const pct = total > 0 ? Math.round((ok / total) * 100) : 0;
  return { ok, total, pct };
}

export interface LeituraAlterada {
  instrumentId: string;
  value: number;
  ts: Date;
}

/**
 * Última leitura efetivamente ALTERADA (D6): a mais recente cujo valor difere da
 * leitura anterior do MESMO instrumento (uma repetição não conta como "nova").
 * Sem mudança confirmável na janela (ex.: 1 leitura por instrumento) → fallback à
 * mais recente. `null` só quando não há leituras.
 */
export function ultimaLeituraAlterada(readings: FeedReadingLike[]): LeituraAlterada | null {
  if (readings.length === 0) return null;
  const sorted = [...readings].sort((a, b) => b.ts.getTime() - a.ts.getTime());
  for (let i = 0; i < sorted.length; i++) {
    const r = sorted[i]!;
    // Leitura anterior (mais antiga) do mesmo instrumento **e do mesmo canal**.
    //
    // GUPM-270 — sem o canal, o tiltímetro comparava inclinação com temperatura
    // como se fossem a mesma série: 0,35° seguido de 11,2 °C acusava "mudou", e o
    // card exibia o valor da temperatura com a unidade de inclinação ("11.2 °").
    // Grandezas diferentes não se comparam nem se rotulam uma pela outra.
    const prior = sorted.slice(i + 1).find(
      (x) => x.instrumentId === r.instrumentId && (x.canal ?? null) === (r.canal ?? null),
    );
    if (prior && prior.value !== r.value) {
      return { instrumentId: r.instrumentId, value: r.value, ts: r.ts };
    }
  }
  const newest = sorted[0]!;
  return { instrumentId: newest.instrumentId, value: newest.value, ts: newest.ts };
}

const DIA_MS = 24 * 60 * 60 * 1000;
const NAO_NORMAL: ReadonlySet<string> = new Set(['ATENCAO', 'ALERTA', 'EMERGENCIA']);

/**
 * Conta leituras INCONSISTENTES (não-normais) numa janela (D7 — "eventos 24h").
 * Só nível em {ATENCAO,ALERTA,EMERGENCIA} dentro de `[now-windowMs, now]`.
 */
export function contarLeiturasInconsistentes(
  readings: LevelReadingLike[],
  now: Date,
  windowMs: number = DIA_MS,
): number {
  const nowMs = now.getTime();
  const since = nowMs - windowMs;
  return readings.filter(
    (r) =>
      r.ts.getTime() >= since &&
      r.ts.getTime() <= nowMs && // janela fechada [now-windowMs, now] (guard contra ts futuro)
      r.level != null &&
      NAO_NORMAL.has(r.level),
  ).length;
}

/** Cadência do cron de ingestão server-side (GUPM-126: a cada 10 minutos). */
export const INGESTAO_CRON_MIN = 10;

export interface CicloCron {
  totalMs: number;
  elapsedMs: number;
  remainingMs: number;
  pct: number;
  proxima: Date;
}

/**
 * Progresso do ciclo do cron de ingestão (D8). O cron a cada N minutos dispara nos
 * múltiplos de N (alinhados ao relógio/epoch). `remainingMs` = ms até o próximo
 * disparo; no exato limite, reinicia (ciclo cheio). `proxima` = timestamp do disparo.
 */
export function cicloCron(now: Date, cronMinutes: number): CicloCron {
  const totalMs = cronMinutes * 60_000;
  const elapsedMs = now.getTime() % totalMs;
  const remainingMs = elapsedMs === 0 ? totalMs : totalMs - elapsedMs;
  const pct = Math.round((elapsedMs / totalMs) * 100);
  return { totalMs, elapsedMs, remainingMs, pct, proxima: new Date(now.getTime() + remainingMs) };
}

// ── Thresholds por Estrutura (Resumo Técnico A/B/C — V8 techTable, L4792) ──────

/** Barragem no mínimo necessário para agrupar/rotular (compatível com `DamOption`). */
export interface DamLike {
  id: string;
  code: string;
  name: string;
}

/** Instrumento no mínimo necessário para separar com/sem limiar por estrutura. */
interface InstThresholdLike {
  id: string;
  damId: string;
  type: string;
  levelAtencaoMax: number | null;
  /** GUPM-270: canais com carta vigente — decide qual leitura governa (multi-canal). */
  canaisComFaixa?: readonly Canal[];
  /**
   * GUPM-307 — declaração de fora de serviço. Obrigatória no shape mínimo desta função
   * porque ela deriva o nível por conta própria (ver `nivelDe` abaixo) e, sem o campo, a
   * tabela do Resumo Técnico afirma o nível congelado de um instrumento retirado.
   */
  foraDeServicoDesde: Date | null;
}

export interface InstrumentoNaSecao<I> {
  instrument: I;
  /**
   * GUPM-230: `SEM_LEITURA` quando ninguém mediu o instrumento — verde afirmaria
   * "medido e dentro da faixa". Mesma regra que o GUPM-228 aplicou na tabela.
   */
  level: NivelExibido;
  /** Valor da leitura atual; `null` sem leitura. */
  value: number | null;
  /** Unidade do tipo do instrumento (para exibir "valor unidade"); '' se desconhecida. */
  unit: string;
}

export interface SecaoBarragem<I> {
  dam: DamLike;
  /** Instrumentos com carta de controle (limiar de atenção definido). */
  comLimiar: InstrumentoNaSecao<I>[];
  /** Instrumentos sem limiar (aguardam Carta de Controle de Deslocamento). */
  semLimiar: I[];
  /**
   * GUPM-270 — tiltímetros, que vão para tabela PRÓPRIA (uma linha, dois eixos).
   *
   * Fora de `comLimiar`/`semLimiar` de propósito: aqueles dois grupos assumem uma
   * leitura e um conjunto de limiares por linha, e ambos os critérios vêm do campo
   * legado `levelAtencaoMax`, que o tiltímetro nunca preenche. Deixá-lo lá o faria
   * aparecer duas vezes, e na tabela principal com os três limiares em "—".
   */
  tiltimetros: I[];
  /**
   * GUPM-329 — marcos superficiais, em tabela PRÓPRIA (ΔLeste/ΔNorte/Recalque).
   *
   * Fora de `comLimiar`/`semLimiar` pela mesma razão do tiltímetro, e mais uma: a
   * campanha topográfica traz três grandezas e o que se exibe é o DESLOCAMENTO desde
   * a implantação, não a leitura. A coordenada absoluta (296.462 m) na coluna
   * "Última leitura" não informaria nada a quem acompanha a barragem.
   */
  marcos: I[];
  hasAnomaly: boolean;
  /**
   * Aberta por padrão. Hoje sempre `true` (decisão do dono em 05/08/2026): quem
   * abre o Dashboard ou o Mapa quer o quadro das três estruturas de uma vez.
   *
   * A regra anterior era `code === 'B' || hasAnomaly`, herdada do V8 — deixava A e
   * C fechadas no caso comum, e um filtro que casasse só com elas parecia não ter
   * feito nada. Continua sendo um campo (e não uma constante no componente) para
   * que uma política futura por estrutura tenha um lugar único no domínio.
   */
  defaultOpen: boolean;
}

/** Quantos instrumentos MONITORADOS da estrutura têm régua, e quantos não têm. */
export interface ContagemDeCarta {
  comCarta: number;
  semCarta: number;
}

/**
 * Contagem de carta do cabeçalho de uma estrutura — GUPM-275.
 *
 * Duas regras, e as duas foram violadas em produção pelo contador que vivia
 * dentro do componente:
 *
 * 1. **Só instrumento ativo conta.** Retirado saiu do inventário GPX v2 e nunca
 *    terá carta ([ADR-0032](../../../docs/ADR/0032-populacao-monitorada-e-instrumento-ativo.md)).
 *    Somá-lo fazia o cabeçalho da Barragem B dizer "21 s/ carta" (3 ativos + 18
 *    retirados) enquanto o card da mesma tela dizia "4".
 * 2. **Tiltímetro (e marco superficial, GUPM-329) conta.** Ele tem tabela própria e por isso fica fora de
 *    `comLimiar`/`semLimiar`, mas existe e é monitorado — ficava fora dos DOIS
 *    lados, e a Barragem A dizia "3 c/ limites" tendo 5.
 *
 * Quem decide "tem carta?" é `temFaixaDeControle`, o predicado único que enxerga
 * carta escalar **e** carta por canal — tiltímetro só tem a segunda.
 *
 * Invariante: `comCarta + semCarta` = população ativa da estrutura. É o que impede
 * o cabeçalho de divergir do denominador do card.
 */
export function contagemDeCartaDaEstrutura<I extends InstLike>(
  secao: Pick<SecaoBarragem<I>, 'comLimiar' | 'semLimiar' | 'tiltimetros' | 'marcos'>,
): ContagemDeCarta {
  // Tiltímetro e marco (GUPM-329) têm tabela própria e ficam fora de
  // `comLimiar`/`semLimiar` — sem somá-los aqui eles sumiam dos DOIS lados.
  const aparteAtivos = [...secao.tiltimetros, ...secao.marcos].filter((i) => i.isActive);
  return {
    comCarta:
      secao.comLimiar.filter((x) => x.instrument.isActive).length +
      aparteAtivos.filter((i) => temFaixaDeControle(i)).length,
    semCarta:
      secao.semLimiar.filter((i) => i.isActive).length +
      aparteAtivos.filter((i) => !temFaixaDeControle(i)).length,
  };
}

const NIVEIS_VALIDOS: ReadonlySet<string> = new Set(['NORMAL', 'ATENCAO', 'ALERTA', 'EMERGENCIA']);

/**
 * Agrupa instrumentos por barragem para o painel "Thresholds por Estrutura" (V8).
 * Separa, por estrutura, os instrumentos COM limiar de atenção dos SEM (carta de
 * deslocamento pendente); `hasAnomaly` = algum instrumento medido fora da faixa;
 * `defaultOpen` = sempre (ver o campo). Só retorna barragens com ≥1 instrumento (sem
 * seção vazia). Ordena por `code` (A, B, C). Genérico: devolve o próprio `Instrument`.
 */
export function agruparInstrumentosPorBarragem<I extends InstThresholdLike>(
  instruments: I[],
  // GUPM-270: `canal` e `ts` entraram porque a escolha da leitura que governa o
  // instrumento passou a depender do canal (e do desempate por recência).
  readings: {
    instrumentId: string; level: string | null; value: number;
    canal: Canal | null; ts: Date;
  }[],
  dams: DamLike[],
): SecaoBarragem<I>[] {
  // GUPM-270 — a leitura que governa cada instrumento. Antes era "a última linha do
  // array vence", o que com três canais no mesmo timestamp é sorteio: o selo de
  // anomalia da estrutura podia ser decidido pela linha de `temperatura`.
  // `leituraGovernante` devolve o pior canal COM FAIXA para multi-canal e a mais
  // recente para canal único.
  const porInstrumento = new Map<string, { level: TarpLevel; value: number }>();
  for (const inst of instruments) {
    const governante = leituraGovernante(
      readings.filter((r) => r.instrumentId === inst.id),
      inst.canaisComFaixa ?? [],
    );
    if (!governante) continue;
    const level = governante.level && NIVEIS_VALIDOS.has(governante.level)
      ? (governante.level as TarpLevel)
      : 'NORMAL';
    porInstrumento.set(inst.id, { level, value: governante.value });
  }
  const instPorId = new Map(instruments.map((i) => [i.id, i]));
  /**
   * Sem leitura ≠ NORMAL (GUPM-230). O `?? 'NORMAL'` que estava aqui pintava de
   * verde quem ninguém mediu, ao lado de uma coluna de leitura mostrando "—".
   *
   * GUPM-307 — a declaração de fora de serviço entra AQUI, e não só no funil
   * `nivelExibidoDeInstrumento`, porque esta função tem a sua própria derivação de nível:
   * lê `leituraGovernante` e mapeia o valor cru. O compilador não tem como acusar a
   * ausência, e no GUPM-303 isto não apareceu porque tiltímetro tem tabela separada e o
   * portão de perda de sinal era só TILT — o defeito só surgiu ao abrir a tela, com 998
   * testes verdes: o botão do header já havia parado de pulsar e a linha do INA-12A na
   * tabela do Resumo Técnico seguia dizendo "Emergência".
   *
   * Só a declaração entra. `SEM_SINAL` e `SEM_FAIXA` continuam fora desta derivação de
   * propósito: `anomalo()` abaixo roda sobre TODOS os instrumentos (inclusive os sem
   * limiar), e trocar isto pelo funil mudaria o selo "⚠️ anomalia" da estrutura para os 44
   * sem carta — que é outro card.
   */
  const nivelDe = (id: string): NivelExibido => {
    const inst = instPorId.get(id);
    if (inst && foraDeServico(inst)) return FORA_DE_SERVICO;
    return porInstrumento.get(id)?.level ?? SEM_LEITURA;
  };
  const valorDe = (id: string): number | null => porInstrumento.get(id)?.value ?? null;
  /**
   * Anomalia da ESTRUTURA = instrumento medido fora da faixa. Falta de leitura
   * não entra: ela abriria as três seções e acenderia o selo "⚠️ anomalia" em
   * todas — com 44 instrumentos sem faixa, o selo deixaria de significar algo.
   */
  const anomalo = (id: string): boolean => {
    const level = nivelDe(id);
    return level === 'ATENCAO' || level === 'ALERTA' || level === 'EMERGENCIA';
  };

  const secoes: SecaoBarragem<I>[] = [];
  for (const dam of [...dams].sort((a, b) => a.code.localeCompare(b.code))) {
    const insts = instruments.filter((i) => i.damId === dam.id);
    if (insts.length === 0) continue;
    // GUPM-270 — tiltímetro tem tabela própria (uma linha, dois eixos): sai dos
    // dois grupos antes de qualquer outra coisa, senão apareceria duas vezes.
    const tiltimetros = insts.filter((i) => i.type === 'TILT');
    // GUPM-329 — marco superficial também tem tabela própria, pelo mesmo motivo e
    // mais um: a campanha traz TRÊS grandezas, e o que se exibe não é a leitura
    // (coordenada UTM absoluta) e sim o deslocamento desde a implantação, em mm.
    const marcos = insts.filter((i) => ehMarco(i.type));
    const demais = insts.filter((i) => i.type !== 'TILT' && !ehMarco(i.type));
    const comLimiar = demais
      .filter((i) => i.levelAtencaoMax != null)
      .map((i) => ({ instrument: i, level: nivelDe(i.id), value: valorDe(i.id), unit: unitForType(i.type) }));
    const semLimiar = demais.filter((i) => i.levelAtencaoMax == null);
    // A anomalia da estrutura continua olhando TODOS os instrumentos, tiltímetro
    // incluído — ele estar em tabela separada não o tira da barragem.
    const hasAnomaly = insts.some((i) => anomalo(i.id));
    secoes.push({ dam, comLimiar, semLimiar, tiltimetros, marcos, hasAnomaly, defaultOpen: true });
  }
  return secoes;
}
